import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store/root-state';

import { DefaultSortSpec, type SortSpec } from '@/domain/shared/sortOptions';
import type { IUtility } from '@/domain/entities/utility';

import {
  createUtility,
  deleteUtility,
  fetchUtilityById,
  fetchUtilitiesByPropertyId,
  updateUtility,
} from './utilitiesThunks';
import type { UtilitiesState } from './utilittiesSliceTypes';

const initialState: UtilitiesState = {
  itemsByPropertyID: {},
  dirtyByPropertyID: {},
  hasFetchedByPropertyID: {},
  isListingByPropertyID: {},
  errorByPropertyID: {},

  selected: null,
  isLoadingOne: false,
  isSaving: false,
  isDeleting: false,
  hasChanges: false,

  activeSort: DefaultSortSpec,
};

const ensureBucket = (state: UtilitiesState, propertyID: string) => {
  if (!state.itemsByPropertyID[propertyID]) state.itemsByPropertyID[propertyID] = [];
  if (state.hasFetchedByPropertyID[propertyID] === undefined) state.hasFetchedByPropertyID[propertyID] = false;
  if (state.isListingByPropertyID[propertyID] === undefined) state.isListingByPropertyID[propertyID] = false;
  if (state.errorByPropertyID[propertyID] === undefined) state.errorByPropertyID[propertyID] = null;
  if (!state.dirtyByPropertyID[propertyID]) state.dirtyByPropertyID[propertyID] = {};
};

const upsertById = (items: IUtility[], rec: IUtility): IUtility[] => {
  const id = rec.id;
  if (!id) return [rec, ...items];
  const idx = items.findIndex((x) => x.id === id);
  if (idx === -1) return [rec, ...items];
  const copy = items.slice();
  copy[idx] = rec;
  return copy;
};

const removeById = (items: IUtility[], id: string): IUtility[] => items.filter((x) => x.id !== id);

export const utilitiesSlice = createSlice({
  name: 'utilities',
  initialState,
  reducers: {
    // ---------- sort ----------
    setUtilitiesActiveSort: (state, action: PayloadAction<SortSpec>) => {
      state.activeSort = action.payload;
    },

    // ---------- editor ----------
    clearSelectedUtility: (state) => {
      state.selected = null;
      state.hasChanges = false;
    },

    patchSelectedUtility: (state, action: PayloadAction<Partial<IUtility>>) => {
      if (!state.selected) return;

      const updated: IUtility = {
        ...state.selected,
        ...action.payload,
      };

      state.selected = updated;
      state.hasChanges = true;

      const propertyID = updated.propertyID;
      if (propertyID) {
        ensureBucket(state, propertyID);
        state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], updated);
      }
    },

    markUtilitiesClean: (state) => {
      state.hasChanges = false;
    },

    // ---------- list/editor helpers (per-item dirty) ----------
    upsertUtilityLocal: (state, action: PayloadAction<{ propertyID: string; item: IUtility }>) => {
      const { propertyID, item } = action.payload;
      ensureBucket(state, propertyID);

      state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], item);

      if (item.id) {
        state.dirtyByPropertyID[propertyID][item.id] = true;
      }
    },

    markUtilityDirty: (state, action: PayloadAction<{ propertyID: string; utilityID: string }>) => {
      const { propertyID, utilityID } = action.payload;
      if (!state.dirtyByPropertyID[propertyID]) state.dirtyByPropertyID[propertyID] = {};
      state.dirtyByPropertyID[propertyID][utilityID] = true;
    },

    markUtilityClean: (state, action: PayloadAction<{ propertyID: string; id: string }>) => {
      const { propertyID, id } = action.payload;
      ensureBucket(state, propertyID);
      state.dirtyByPropertyID[propertyID][id] = false;
    },

    clearUtilitiesDirtyForProperty: (state, action: PayloadAction<{ propertyID: string }>) => {
      const { propertyID } = action.payload;
      ensureBucket(state, propertyID);
      state.dirtyByPropertyID[propertyID] = {};
    },

    setUtilitiesForProperty: (state, action: PayloadAction<{ propertyID: string; items: IUtility[] }>) => {
      const { propertyID, items } = action.payload;
      ensureBucket(state, propertyID);
      state.itemsByPropertyID[propertyID] = items;
      state.hasFetchedByPropertyID[propertyID] = true;
      state.errorByPropertyID[propertyID] = null;
      // optional: do NOT auto-clear dirty (same as systems)
      // state.dirtyByPropertyID[propertyID] = {};
    },
  },

  extraReducers: (builder) => {
    builder
      // ---------------- list by property ----------------
      .addCase(fetchUtilitiesByPropertyId.pending, (state, action) => {
        const propertyID = action.meta.arg.propertyID;
        ensureBucket(state, propertyID);
        state.isListingByPropertyID[propertyID] = true;
        state.errorByPropertyID[propertyID] = null;
      })
      .addCase(fetchUtilitiesByPropertyId.fulfilled, (state, action) => {
        const propertyID = action.meta.arg.propertyID;
        ensureBucket(state, propertyID);

        state.isListingByPropertyID[propertyID] = false;
        state.itemsByPropertyID[propertyID] = action.payload;
        state.hasFetchedByPropertyID[propertyID] = true;
      })
      .addCase(fetchUtilitiesByPropertyId.rejected, (state, action) => {
        const propertyID = action.meta.arg.propertyID;
        ensureBucket(state, propertyID);

        state.isListingByPropertyID[propertyID] = false;
        state.hasFetchedByPropertyID[propertyID] = true;
        state.errorByPropertyID[propertyID] =
          (action.payload as { message?: string } | undefined)?.message ?? action.error.message ?? 'Failed to fetch';
      })

      // ---------------- get one ----------------
      .addCase(fetchUtilityById.pending, (state) => {
        state.isLoadingOne = true;
      })
      .addCase(fetchUtilityById.fulfilled, (state, action) => {
        state.isLoadingOne = false;

        const rec = action.payload;
        state.selected = rec;
        state.hasChanges = false;

        const propertyID = rec.propertyID;
        if (propertyID) {
          ensureBucket(state, propertyID);
          state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], rec);
        }
      })
      .addCase(fetchUtilityById.rejected, (state) => {
        state.isLoadingOne = false;
      })

      // ---------------- create ----------------
      .addCase(createUtility.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(createUtility.fulfilled, (state, action) => {
        state.isSaving = false;

        const rec = action.payload.data as IUtility;
        state.selected = rec;
        state.hasChanges = false;

        const propertyID = rec.propertyID;
        if (!propertyID) return;

        ensureBucket(state, propertyID);

        const tempId = action.meta.arg.tempId;

        if (tempId) {
          const idx = state.itemsByPropertyID[propertyID].findIndex((x) => x.id === tempId);

          if (idx !== -1) {
            // ✅ replace temp in-place (no duplication)
            state.itemsByPropertyID[propertyID][idx] = rec;

            // ✅ cleanup temp dirty entry
            delete state.dirtyByPropertyID[propertyID][tempId];
          } else {
            // fallback
            state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], rec);
          }
        } else {
          state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], rec);
        }

        state.hasFetchedByPropertyID[propertyID] = true;

        // ✅ mark real id as clean
        if (rec.id) state.dirtyByPropertyID[propertyID][rec.id] = false;
      })
      .addCase(createUtility.rejected, (state) => {
        state.isSaving = false;
      })

      // ---------------- update ----------------
      .addCase(updateUtility.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updateUtility.fulfilled, (state, action) => {
        state.isSaving = false;

        const rec = action.payload;
        state.selected = rec;
        state.hasChanges = false;

        const propertyID = rec.propertyID;
        if (propertyID) {
          ensureBucket(state, propertyID);
          state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], rec);
          if (rec.id) state.dirtyByPropertyID[propertyID][rec.id] = false;
        }
      })
      .addCase(updateUtility.rejected, (state) => {
        state.isSaving = false;
      })

      // ---------------- delete ----------------
      .addCase(deleteUtility.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteUtility.fulfilled, (state, action) => {
        state.isDeleting = false;

        const { id, propertyID } = action.meta.arg;
        if (propertyID) {
          ensureBucket(state, propertyID);
          state.itemsByPropertyID[propertyID] = removeById(state.itemsByPropertyID[propertyID], id);
          delete state.dirtyByPropertyID[propertyID][id];
        }

        if (state.selected?.id === id) {
          state.selected = null;
          state.hasChanges = false;
        }
      })
      .addCase(deleteUtility.rejected, (state) => {
        state.isDeleting = false;
      });
  },
});

export const {
  setUtilitiesActiveSort,
  clearSelectedUtility,
  patchSelectedUtility,
  markUtilitiesClean,
  setUtilitiesForProperty,
  upsertUtilityLocal,
  markUtilityDirty,
  markUtilityClean,
  clearUtilitiesDirtyForProperty,
} = utilitiesSlice.actions;

export default utilitiesSlice.reducer;

/* ---------------- selectors ---------------- */

const selectUtilitiesState = (state: RootState) => state.utilities;

export const selectUtilitiesActiveSort = createSelector([selectUtilitiesState], (s) => s.activeSort);

export const selectUtilitiesByPropertyId = createSelector(
  [selectUtilitiesState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return [];
    return s.itemsByPropertyID[propertyID] ?? [];
  },
);

export const selectUtilitiesListingByPropertyId = createSelector(
  [selectUtilitiesState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return false;
    return s.isListingByPropertyID[propertyID] ?? false;
  },
);

export const selectUtilitiesFetchedByPropertyId = createSelector(
  [selectUtilitiesState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return false;
    return s.hasFetchedByPropertyID[propertyID] ?? false;
  },
);

export const selectUtilitiesDirtyByPropertyId = createSelector(
  [selectUtilitiesState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return {};
    return s.dirtyByPropertyID[propertyID] ?? {};
  },
);

export const selectUtilityIsDirty = createSelector(
  [selectUtilitiesState, (_: RootState, propertyID?: string | null, id?: string | null) => ({ propertyID, id })],
  (s, { propertyID, id }) => {
    if (!propertyID || !id) return false;
    return s.dirtyByPropertyID[propertyID]?.[id] ?? false;
  },
);

export const selectUtilitiesDirtyIds = createSelector(
  [selectUtilitiesState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return [];
    const map = s.dirtyByPropertyID[propertyID] ?? {};
    return Object.keys(map).filter((id) => map[id]);
  },
);
